import React, { useEffect, useMemo } from 'react';
import { Alert, Box, Button, CircularProgress, Stack, TextField } from '@mui/material';
import { useGetEmployeesQuery } from '../../comments/api/commentsApi';
import type { MentionUser } from '../../comments/types';
import { useUserProfile } from '../../user/hooks/useUserProfile';
import { AnonymousNotice } from './AnonymousNotice';
import { ColleagueSelector } from './ColleagueSelector';
import { RatingCategoriesForm } from './RatingCategoriesForm';

type GiveFeedbackTabProps = {
  selectedColleague: MentionUser | null;
  onColleagueChange: (colleague: MentionUser | null) => void;
  ratings: Record<string, number>;
  onRatingChange: (key: string, value: number | null) => void;
  feedbackText: string;
  onFeedbackTextChange: (text: string) => void;
  formError: string | null;
  submitting: boolean;
  isFormValid: boolean;
  onSubmit: () => void;
  onReset: () => void;
};

export const GiveFeedbackTab: React.FC<GiveFeedbackTabProps> = ({
  selectedColleague,
  onColleagueChange,
  ratings,
  onRatingChange,
  feedbackText,
  onFeedbackTextChange,
  formError,
  submitting,
  isFormValid,
  onSubmit,
  onReset,
}) => {
  const { data: employees = [], isLoading: loadingEmployees } = useGetEmployeesQuery();
  const { userProfile } = useUserProfile();

  const colleagueOptions = useMemo(() => {
    if (!userProfile?.Id) return employees;
    const currentId = String(userProfile.Id);
    return employees.filter((e) => e.id !== currentId);
  }, [employees, userProfile]);

  useEffect(() => {
    if (!userProfile?.Id || !selectedColleague) return;
    if (selectedColleague.id === userProfile.Id.toString()) {
      onColleagueChange(null);
    }
  }, [userProfile?.Id, selectedColleague, onColleagueChange]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <AnonymousNotice />

      <ColleagueSelector
        employees={colleagueOptions}
        loading={loadingEmployees}
        value={selectedColleague}
        onChange={onColleagueChange}
      />

      {selectedColleague && (
        <RatingCategoriesForm
          colleagueFirstName={selectedColleague.name.split(' ')[0] ?? selectedColleague.name}
          ratings={ratings}
          onRatingChange={onRatingChange}
        />
      )}

      <TextField
        label="Anonymous message (optional)"
        placeholder="Write something kind or constructive…"
        multiline
        minRows={2}
        maxRows={4}
        size="small"
        value={feedbackText}
        onChange={(e) => onFeedbackTextChange(e.target.value)}
        inputProps={{ maxLength: 500 }}
        helperText={`${feedbackText.length}/500`}
      />

      {formError && (
        <Alert severity="warning" sx={{ py: 0 }}>
          {formError}
        </Alert>
      )}

      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <Button size="small" variant="text" onClick={onReset} disabled={submitting}>
          Clear
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={onSubmit}
          disabled={!isFormValid || submitting}
          startIcon={submitting ? <CircularProgress size={14} color="inherit" /> : undefined}
        >
          {submitting ? 'Sending…' : 'Send Anonymously'}
        </Button>
      </Stack>
    </Box>
  );
};
